
'use client'

import { memo, useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { motion } from 'motion/react'
import { vw, mq, getTransition, getP18 } from '@/styles'
import { usePageDesign, useTranslator } from '@/hooks'
import { useParams } from 'next/navigation'
import { fetchAPI } from '@/api'
import { CardDownload } from './CardDownload'
import type { TypeCmsContentPage } from '@/schema'


type SectionListType = {
  downloads: TypeCmsContentPage['downloads']
}


const getItems = (items) => items.map((item, idx) => {
  if(!item) return <></>
  return (
    <CardDownload key={`item-download-${idx}`} data={item} idx={`item-download-${idx}`}/>
  )
})


let donwloadsClientUpdated = false

export const SectionListDownload = memo(({ downloads }:SectionListType) => {
  const params = useParams() as { lang: string; slug?: string } || { lang: 'en' }
  const { lang } = params
  const { t } = useTranslator()
  const [downloadFiles, setDownloadFiles] = useState(downloads ?? [])
  const Items = useMemo(() => getItems(downloadFiles), [downloadFiles])
  const { setHeaderTextColor, setPageHasHeaderBgImage } = usePageDesign()

  useEffect(() => {
    setHeaderTextColor('dark')
    setPageHasHeaderBgImage(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  useEffect(() => {
    const getDownloadFiles = async () => {
      try {
        const res = await fetchAPI('get-downloads', { lang: lang }, {
          method: 'GET',
          cache: 'force-cache',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          next: {
            revalidate: 300,
          }
        }) as Response
        if (res.ok) {
          const data = await res.json()
          if (Array.isArray(data?.downloads)) setDownloadFiles(data?.downloads ?? downloads)
          donwloadsClientUpdated = true

        } else {
          return []
        }
      }
      catch (e) {
        console.warn('page get-downloads - Catch e', e)
      }
    }
    if(!donwloadsClientUpdated) getDownloadFiles()
  }, [setDownloadFiles, lang, downloads])


  return (
    <Section>
      {Items.length > 0 ?
        <Ol $hasFiltersApplied={false} $isDownloadPage={true} initial={'inactive'} animate={'active'} exit={'inactive'} variants={variants} key={Items.length}>
          {Items}
        </Ol>
        :
        <p>{t('project.grid.no_downloads')}</p>
      }
    </Section>
  )
})


const Section = styled.section`
  margin-top: ${vw(100, 'mobile')};
  margin-bottom: ${vw(100, 'mobile')};
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('tablet')}{
    margin-top: ${vw(30, 'desktop')};
    margin-bottom: ${vw(130, 'desktop')};
  }

  p {
    ${getP18()}
    opacity: .5;
    padding: ${vw(120, 'mobile')} ${vw(60, 'mobile')};
    text-align: center;

    ${mq.greaterThan('tablet')}{
      padding: ${vw(120, 'desktop')} ${vw(60, 'desktop')};
    }
  }
`

const Ol = styled(motion.ol) <{ $hasFiltersApplied: boolean, $isDownloadPage: boolean }>`
  column-gap: ${vw(23, 'mobile')};
  display: grid;
  flex-wrap: wrap;
  grid: auto-flow / repeat(6, 1fr);
  padding: 0 ${vw(23, 'mobile')};

  ${mq.greaterThan('tablet')}{
    column-gap: ${vw(25, 'desktop')};
    grid: auto-flow / repeat(12, 1fr);
    padding: 0 ${vw(40, 'desktop')};
  }

  > li {
    grid-column: auto / span 3;

    &:nth-child(3n) {
      grid-column: 1 / span 6;
    }

    ${mq.greaterThan('tablet')}{
        &:nth-child(3n) {
        grid-column: auto / span 3;
      }

      ${({ $hasFiltersApplied, $isDownloadPage }) => { if(!$hasFiltersApplied && !$isDownloadPage) { return css`
        &:nth-child(12n+1),
        &:nth-child(12n+8),
        &:nth-child(12n+9),
        &:nth-child(12n+11) {
          grid-column: auto / span 6;
        }
      `}}}
    }
  }
`

const variants = {
  active: {
    opactiy: 1,
    transition: {
      ...getTransition()
    }
  },
  inactive: {
    opactiy: 0,
    transition: {
      ...getTransition()
    }
  }
}

